import { Routes, Route, NavLink } from "react-router-dom";
import { createSDK } from "@godgpt/core";
import { AuthModule } from "@godgpt/auth";
import { I18nModule } from "@godgpt/i18n";
import { SDKProvider } from "@godgpt/react";

import { AuthPlayground } from "./pages/AuthPlayground";
import { ApiPlayground } from "./pages/ApiPlayground";
import { I18nPlayground } from "./pages/I18nPlayground";
import { Home } from "./pages/Home";

// Create SDK instance with debug mode
const sdk = createSDK({ debug: true });

// Register Auth module
const auth = sdk.use(new AuthModule());

// Register mock provider for testing
auth.registerProvider("mock", {
  isAvailable: () => true,
  async signIn() {
    await new Promise((r) => setTimeout(r, 800));
    return {
      success: true,
      state: "success" as const,
      tokens: {
        accessToken: "mock_token_" + Date.now(),
        tokenType: "Bearer",
        expiresIn: 3600,
      },
      user: {
        id: "user_" + Math.random().toString(36).slice(2, 9),
        email: "test@example.com",
        name: "Test User",
        provider: "mock",
      },
    };
  },
  async signOut() {
    await new Promise((r) => setTimeout(r, 300));
  },
});

// Register I18n module
sdk.use(
  new I18nModule({
    defaultLocale: "en",
    supportedLocales: ["en", "es", "fr", "de"],
    fallbackLocale: "en",
    loadResources: async (locale) => {
      const resources: Record<string, Record<string, string>> = {
        en: {
          "nav.home": "Home",
          "nav.auth": "Auth",
          "nav.api": "API",
          "nav.i18n": "i18n",
          "common.loading": "Loading...",
          "common.signIn": "Sign In",
          "common.signOut": "Sign Out",
        },
        es: {
          "nav.home": "Inicio",
          "nav.auth": "Auth",
          "nav.api": "API",
          "nav.i18n": "i18n",
          "common.loading": "Cargando...",
          "common.signIn": "Iniciar Sesión",
          "common.signOut": "Cerrar Sesión",
        },
        fr: {
          "nav.home": "Accueil",
          "nav.auth": "Auth",
          "nav.api": "API",
          "nav.i18n": "i18n",
          "common.loading": "Chargement...",
          "common.signIn": "Se Connecter",
          "common.signOut": "Se Déconnecter",
        },
        de: {
          "nav.home": "Startseite",
          "nav.auth": "Auth",
          "nav.api": "API",
          "nav.i18n": "i18n",
          "common.loading": "Laden...",
          "common.signIn": "Anmelden",
          "common.signOut": "Abmelden",
        },
      };
      return resources[locale] || resources.en;
    },
  })
);

function Navigation() {
  return (
    <nav className="nav">
      <div className="nav-brand">
        <span className="nav-logo">⚡</span>
        <span className="nav-title">SDK Playground</span>
      </div>
      <div className="nav-links">
        <NavLink to="/" end className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          Home
        </NavLink>
        <NavLink to="/auth" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          Auth
        </NavLink>
        <NavLink to="/api" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          API
        </NavLink>
        <NavLink to="/i18n" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
          i18n
        </NavLink>
      </div>
      <div className="nav-status">
        <span className="status-dot"></span>
        <span className="status-text">SDK Ready</span>
      </div>
    </nav>
  );
}

export default function App() {
  return (
    <SDKProvider
      sdk={sdk}
      onReady={() => console.log("[SDK Playground] SDK initialized")}
      onError={(error) => console.error("[SDK Playground] SDK error:", error)}
    >
      <div className="app">
        <Navigation />
        <main className="main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<AuthPlayground />} />
            <Route path="/api" element={<ApiPlayground />} />
            <Route path="/i18n" element={<I18nPlayground />} />
          </Routes>
        </main>
      </div>
    </SDKProvider>
  );
}
